import Alpine from "alpinejs"

const STEP_DELAY = 1000

const TEXT_BRANCH_RULES = {
  2: {
    basedOnQuestion: 1,
    text1Answers: ["question_1.answer_1", "question_1.answer_2"],
    text2Answers: ["question_1.answer_3", "question_1.answer_4"],
    text3Answers: [],
  },
  3: {
    basedOnQuestion: 2,
    text1Answers: ["question_2.answer_1", "question_2.answer_2"],
    text2Answers: ["question_2.answer_3", "question_2.answer_4"],
    text3Answers: ["question_2.answer_5"],
  },
  4: {
    basedOnQuestion: 3,
    text1Answers: ["question_3.answer_1"],
    text2Answers: ["question_3.answer_2", "question_3.answer_3"],
    text3Answers: ["question_3.answer_4"],
  },
  5: {
    basedOnQuestion: 4,
    text1Answers: ["question_4.answer_1"],
    text2Answers: ["question_4.answer_2", "question_4.answer_3"],
    text3Answers: ["question_4.answer_4"],
  },
  6: {
    basedOnQuestion: 5,
    text1Answers: ["question_5.answer_1"],
    text2Answers: ["question_5.answer_2"],
    text3Answers: [],
  },
}

export default (props) => ({
  totalQuestions: props.questions || 6,
  currentQuestion: 1,
  // Phase model (for 3 messages):
  //   0 = initial (nothing visible)
  //   1 = loader before msg 0
  //   2 = msg 0 visible + loader before msg 1
  //   3 = msgs 0,1 visible + loader before msg 2
  //   4 = all msgs visible, interactive
  revealPhase: 0,
  activeMessageCount: 0,
  isRevealing: false,
  interactive: false,
  revealTimerId: null,

  init() {
    this.store = Alpine.store("data")
    this.restoreFromStore()
  },

  restoreFromStore() {
    const savedAnswers = this.store.values.nutritionistChatAnswers

    if (savedAnswers.length > 0) {
      this.currentQuestion = Math.min(savedAnswers.length + 1, this.totalQuestions)
      this.jumpToFullyRevealedState()
    } else {
      this.currentQuestion = 1
      this.startRevealSequence()
    }
  },

  syncScroll(behavior = "smooth") {
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        const root = this.$el
        if (!root?.querySelector) return

        const content = this.chatContentElement

        if (content) {
          content.scrollTo({
            top: content.scrollHeight,
            behavior,
          })
        }

        window.scrollTo({
          top: document.documentElement.scrollHeight - window.innerHeight,
          behavior,
        })
      })
    })
  },

  jumpToFullyRevealedState() {
    clearTimeout(this.revealTimerId)
    this.revealTimerId = null
    this.isRevealing = false
    this.interactive = true
    this.activeMessageCount = this.getQuestionMessagePlan(this.currentQuestion).length
    this.revealPhase = this.activeMessageCount + 1
    this.syncScroll("instant")
  },

  startRevealSequence() {
    clearTimeout(this.revealTimerId)
    this.isRevealing = true
    this.interactive = false
    this.revealPhase = 1
    this.activeMessageCount = this.getQuestionMessagePlan(this.currentQuestion).length
    this.syncScroll()
    this.scheduleNextReveal()
  },

  scheduleNextReveal() {
    const finalPhase = this.activeMessageCount + 1

    this.revealTimerId = setTimeout(() => {
      this.revealPhase++

      if (this.revealPhase < finalPhase) {
        this.scheduleNextReveal()
      } else {
        this.isRevealing = false
        this.interactive = true
        this.syncScroll()
      }
    }, STEP_DELAY)
  },

  getSelectedAnswerForQuestion(questionIndex) {
    const prefix = "question_" + questionIndex + ".answer_"
    return this.store.values.nutritionistChatAnswers.find((answer) => answer.startsWith(prefix)) || null
  },

  getQuestionMessagePlan(questionIndex) {
    const baseKey = "question_" + questionIndex

    if (questionIndex === 1) {
      return this.store.values.additionalGoal[0] === "additionalGoal.answer_9" ?
      [baseKey + ".text_2", baseKey + ".question"] :
      [baseKey + ".text_1", baseKey + ".text_2", baseKey + ".question"]
    }

    const rule = TEXT_BRANCH_RULES[questionIndex]
    const selectedAnswer = rule
      ? this.getSelectedAnswerForQuestion(rule.basedOnQuestion)
      : null
    const isText1 = selectedAnswer
      ? rule.text1Answers.includes(selectedAnswer)
      : true
    const isText2 = selectedAnswer
      ? rule.text2Answers.includes(selectedAnswer)
      : true

    const textKey = baseKey + "." + (isText1 ? "text_1" : isText2 ? "text_2" : "text_3" )

    if (questionIndex === 6 && isText2 ) {
      return [baseKey + ".text_2"]
    }

    return [textKey, baseKey + ".question"]
  },

  getMessagePlanIndex(questionIndex, messageKey) {
    return this.getQuestionMessagePlan(questionIndex).indexOf(messageKey)
  },

  isLoaderForQuestionMessage(questionIndex, messageKey) {
    const msgIndex = this.getMessagePlanIndex(questionIndex, messageKey)

    if (msgIndex < 0 || !this.isCurrentQuestion(questionIndex)) {
      return false
    }

    return this.isRevealing && this.revealPhase === msgIndex + 1
  },

  isQuestionMessageVisible(questionIndex, messageKey) {
    const msgIndex = this.getMessagePlanIndex(questionIndex, messageKey)

    if (msgIndex < 0) return false

    if (!this.isCurrentQuestion(questionIndex)) return true

    return this.revealPhase >= msgIndex + 2
  },

  isCurrentQuestion(questionIndex) {
    return this.currentQuestion === questionIndex
  },

  showQuestionBlock(questionIndex) {
    return this.currentQuestion >= questionIndex
  },

  showAnswerOptions(questionIndex) {
    return this.isCurrentQuestion(questionIndex)
  },

  isAnswerDisabled() {
    return !this.interactive
  },

  showCustomerReply(answerKey) {
    return this.store.values.nutritionistChatAnswers.includes(answerKey)
  },

  showContinueButton() {
    return this.currentQuestion === this.totalQuestions
  },

  updateChat(answerKey) {
    if (!this.interactive) return

    this.store.values.nutritionistChatAnswers.push(answerKey)

    if (this.currentQuestion >= this.totalQuestions) {
      this.interactive = false
      this.currentQuestion++
      return
    }

    this.currentQuestion++
    this.startRevealSequence()
  }
})